import { FC, useEffect, useMemo, useState } from 'react'
import cn from 'classnames'
import { useAsyncRetry } from 'react-use'
import Modal from '@common/Modal'
import Button from '@UI/New/Button'
import Select from '../base/Select/Select'
import { IcoRemove } from '@/utils/icons'
import { crmApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useCRMContext } from '../../CRMContext'
import { getLeadFieldValue, type LeadFieldsSource } from '../../utils/leadFields'
import mclasses from '../../styles/modal.module.css'
import classes from './index.module.css'

/** Что реально нужно модалке: id лида, поля для ФИО, список воронок с бэка. */
type RemoveFunnelFromLeadsModalLead = LeadFieldsSource & {
  id: number
  funnels?: ReadonlyArray<{ id: number }>
}

interface RemoveFunnelFromLeadsModalProps {
  lead?: RemoveFunnelFromLeadsModalLead | RemoveFunnelFromLeadsModalLead[]
  open: boolean
  onClose: () => void
  onSuccess?: (payload?: { removedFunnelId: number }) => void
}

type FunnelOption = {
  value: number
  label: string
}

const RemoveFunnelFromLeadsModal: FC<RemoveFunnelFromLeadsModalProps> = ({
  lead,
  open,
  onClose,
  onSuccess,
}) => {
  const addAlert = useAddAlert()
  const {
    preloadingData: { funnels },
  } = useCRMContext()
  const funnelsState = useAsyncRetry(
    async () => await crmApi.funnels.getAllFunnels(),
    [open],
  )
  const actualFunnels = funnelsState.value
    ? funnelsState.value?.data?.data?.funnels ?? []
    : funnels
  const [funnelId, setFunnelId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setFunnelId(null)
  }, [open])

  useEffect(() => {
    if (
      open &&
      (lead === undefined || (Array.isArray(lead) && lead.length === 0))
    ) {
      onClose()
    }
  }, [open, lead, onClose])

  useEffect(() => {
    if (!open || !funnelsState.error) return
    addAlert({
      title: 'Ошибка',
      text: 'Не удалось загрузить список воронок',
      type: 'error',
    })
  }, [addAlert, funnelsState.error, open])

  const leadNames = useMemo<string[]>(() => {
    if (!lead) return ['-']
    if (Array.isArray(lead)) {
      return lead.reduce((acc, item) => {
        const name = getLeadFieldValue(item, 'NAME') || '(без фио)'
        if (acc.includes(name)) return acc
        return [...acc, name]
      }, [] as string[])
    }
    return [getLeadFieldValue(lead, 'NAME') || '(без фио)']
  }, [lead])

  const selectedLeads = useMemo<RemoveFunnelFromLeadsModalLead[]>(
    () => (Array.isArray(lead) ? lead : lead ? [lead] : []),
    [lead],
  )

  const availableLeadFunnelIds = useMemo<number[]>(() => {
    const ids = new Set<number>()
    for (const row of selectedLeads) {
      for (const f of row.funnels ?? []) {
        ids.add(f.id)
      }
    }
    return Array.from(ids)
  }, [selectedLeads])

  const funnelOptions = useMemo<FunnelOption[]>(
    () =>
      actualFunnels
        .filter((funnel) => availableLeadFunnelIds.includes(funnel.id))
        .map((funnel) => ({
          value: funnel.id,
          label: funnel.name,
        })),
    [actualFunnels, availableLeadFunnelIds],
  )

  const handleConfirm = async () => {
    if (!funnelId) {
      addAlert({
        title: 'Ошибка',
        text: 'Выберите воронку',
        type: 'error',
      })
      return
    }

    try {
      setSubmitting(true)

      const leadIds = selectedLeads.map((item) => item.id)
      if (!leadIds.length) throw new Error('No leads selected')

      const response = await crmApi.leads.bulkRemoveFunnelFromLeads({
        lead_ids: leadIds,
        funnel_id: funnelId,
      })
      if (isError(response)) throw new Error()

      const result = response.data?.data
      const removedCount = result?.removed_count ?? 0
      const skippedCount = result?.skipped_single_funnel_count ?? 0
      const notFoundCount = result?.not_found_count ?? 0

      const messageParts: string[] = []
      if (removedCount > 0) {
        messageParts.push(`Удалено у лидов: ${removedCount}`)
      }
      if (skippedCount > 0) {
        messageParts.push(
          `Пропущено (у лида только одна воронка): ${skippedCount}`,
        )
      }
      if (notFoundCount > 0) {
        messageParts.push(`Не найдено лидов: ${notFoundCount}`)
      }

      const message =
        messageParts.length > 0
          ? messageParts.join('\n')
          : 'Изменений не произошло'

      addAlert({
        title: removedCount > 0 ? 'Успешно' : 'Без изменений',
        text: message,
        type: removedCount > 0 ? 'success' : 'error',
      })

      if (removedCount > 0) {
        onSuccess && onSuccess({ removedFunnelId: funnelId })
      }
    } catch (error: unknown) {
      const backendError = (
        error as { response?: { data?: { error?: unknown } } }
      )?.response?.data?.error
      const errorText = Array.isArray(backendError)
        ? backendError.join('\n')
        : typeof backendError === 'string'
        ? backendError
        : 'Не удалось удалить воронку у выбранных лидов'

      addAlert({
        title: 'Ошибка',
        text: errorText,
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (!funnelId) return
    const allowedIds = new Set(funnelOptions.map((option) => option.value))
    if (!allowedIds.has(funnelId)) {
      setFunnelId(null)
    }
  }, [funnelId, funnelOptions])

  return (
    <Modal
      active={open}
      className={cn(mclasses.modalWrapper, classes.modalWrapper) as any}
      setActive={() => {
        if (submitting) return
        onClose()
      }}
    >
      <div className={mclasses.modalContainer}>
        <div className={mclasses.modalHeader}>
          <div className={mclasses.modalTitleContainer}>
            <span className={mclasses.modalHeaderTitle}>
              Выберите воронку для удаления у {Array.isArray(lead) ? 'лидов' : 'лида'}
            </span>
          </div>
          <div
            className={mclasses.closeIcon}
            onClick={() => {
              if (submitting) return
              onClose()
            }}
          >
            <IcoRemove />
          </div>
        </div>

        <div className={cn(mclasses.formWrapper, classes.formWrapper)}>
          <div>
            <p className={mclasses.label}>
              {Array.isArray(lead) ? 'Выбранные лиды' : 'Выбранный лид'}
            </p>
            <div className={classes.leadsList}>
              {leadNames.map((name, index) => (
                <p key={`${name}-${index}`} className={classes.leadItem}>
                  {name}
                </p>
              ))}
            </div>
          </div>

          <p className={classes.warningText}>
            Если у лида привязана только одна воронка, удаление не выполняется.
          </p>

          <div>
            <p className={mclasses.label}>Выберите воронку</p>
            <Select
              showSearch
              placeholder="Выберите воронку"
              value={funnelId ?? undefined}
              onChange={(value) => setFunnelId(Number(value))}
              options={funnelOptions}
              disabled={submitting}
            />
          </div>
        </div>

        <div className={mclasses.actions}>
          <Button color="gray" onClick={onClose} disabled={submitting}>
            Отменить
          </Button>
          <Button
            color="primary"
            onClick={handleConfirm}
            disabled={!funnelId || submitting}
            isLoading={submitting}
          >
            Удалить
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default RemoveFunnelFromLeadsModal
